import React, { useCallback, useMemo, useState } from "react";
import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { Breadcrumb, Typography } from "antd";
import type { BreadcrumbProps } from "antd";
import { useOptionalRouter } from "./useOptionalRouter";

const { Title: AntTitle, Text } = Typography;

export type PageLayoutHeaderConfig = {
	title?: ReactNode;
	secondaryTitle?: ReactNode;
	actions?: ReactNode;
};

type PageLayoutContextValue = {
	setHeader: (config: PageLayoutHeaderConfig) => void;
	clearHeader: () => void;
};

export const PageLayoutContext = React.createContext<PageLayoutContextValue | null>(null);

export interface PageLayoutProps {
	breadcrumbItems?: BreadcrumbProps["items"];
	breadcrumbStyle?: CSSProperties;
	headerContent?: ReactNode;
	actions?: ReactNode;
	children: ReactNode;
	maxWidth?: number | string;
	containerStyle?: CSSProperties;
	wrapperStyle?: CSSProperties;
}

export function PageLayout({
	breadcrumbItems,
	breadcrumbStyle,
	headerContent,
	actions,
	children,
	maxWidth = "1100px",
	containerStyle,
	wrapperStyle,
}: PageLayoutProps) {
	const { navigate } = useOptionalRouter();
	const [headerConfig, setHeaderConfig] = useState<PageLayoutHeaderConfig>({});

	const setHeader = useCallback((config: PageLayoutHeaderConfig) => {
		setHeaderConfig(config);
	}, []);

	const clearHeader = useCallback(() => {
		setHeaderConfig({});
	}, []);

	const normalizedBreadcrumbItems = useMemo(() => {
		if (!breadcrumbItems || breadcrumbItems.length === 0) {
			return breadcrumbItems;
		}

		const isExternalHref = (href: string) =>
			/^https?:\/\//.test(href) || href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("//");

		return breadcrumbItems.map((item) => {
			const { href } = item;
			if (typeof href !== "string" || isExternalHref(href)) return item;

			const userOnClick = item.onClick;
			const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
				event.preventDefault();
				userOnClick?.(event);
				navigate(href);
			};

			return {
				...item,
				onClick: handleClick,
			};
		});
	}, [breadcrumbItems, navigate]);

	const hasBreadcrumbs = Boolean(breadcrumbItems && breadcrumbItems.length);
	const headerActions = headerConfig.actions || actions;
	const showTitle = Boolean(headerConfig.title);
	const showSecondaryRow = Boolean(headerConfig.secondaryTitle) || Boolean(headerActions);
	const hasHeaderRow = showTitle || showSecondaryRow || hasBreadcrumbs || Boolean(actions);

	const contextValue = useMemo(
		() => ({
			setHeader,
			clearHeader,
		}),
		[setHeader, clearHeader],
	);

	return (
		<PageLayoutContext.Provider value={contextValue}>
			<div style={{ width: "100%", padding: "24px 0", ...wrapperStyle }}>
				<div
					style={{
						width: "100%",
						maxWidth,
						margin: "0 auto",
						padding: "0 16px",
						...containerStyle,
					}}
				>
					{hasHeaderRow && (
						<div style={{ marginBottom: headerContent ? 8 : 16 }}>
							{showTitle && (
								<AntTitle level={2} style={{ margin: 0, fontWeight: 600 }}>
									{headerConfig.title}
								</AntTitle>
							)}

							{showSecondaryRow && (
								<div
									style={{
										display: "flex",
										flexWrap: "wrap",
										gap: 12,
										alignItems: "center",
										marginTop: showTitle ? 8 : 0,
									}}
								>
									{headerConfig.secondaryTitle && (
										<Text type="secondary" style={{ fontSize: 16 }}>
											{headerConfig.secondaryTitle}
										</Text>
									)}
									{headerActions && (
										<div
											style={{
												marginLeft: headerConfig.secondaryTitle ? "auto" : 0,
												flexShrink: 0,
											}}
										>
											{headerActions}
										</div>
									)}
								</div>
							)}

							{hasBreadcrumbs && (
								<div style={{ marginTop: showTitle || showSecondaryRow ? 16 : 0 }}>
									<Breadcrumb
										items={normalizedBreadcrumbItems}
										style={{ marginBottom: 0, ...breadcrumbStyle }}
									/>
								</div>
							)}
						</div>
					)}

					{headerContent && <div style={{ marginBottom: 16 }}>{headerContent}</div>}

					<div>{children}</div>
				</div>
			</div>
		</PageLayoutContext.Provider>
	);
}
